import React from "react";
import { Link } from "react-router-dom";
import QuoteBlock from "../components/QuoteBlock";
import { useFadeIn } from "../Hooks/useFadeIn";
import { Helmet } from "react-helmet-async";
import OfferCards from "./offers/OfferCards";
import PerformanceBufferSection from "./preparation/PerformanceBufferSection";

import VideoPreview from "./VideoPreview";

const pricingServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Mission Movement Aanbod",
  url: "https://missionmovement.nl/pricing",
  provider: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
  areaServed: {
    "@type": "Country",
    name: "Netherlands",
  },
  serviceType:
    "Online voorbereiding en coaching voor Defensie en veiligheidsdiensten",
  description:
    "Bekijk het Mission Movement aanbod voor kandidaten die serieus richting Defensie, politie, brandweer, KMar of specialistische eenheden willen werken.",
  offers: [
    {
      "@type": "Offer",
      name: "Zelfstandig 12-weeks programma",
      url: "https://missionmovement.nl/pricing",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      price: "89",
      description:
        "Vaste 12-weekse structuur voor zelfstandige voorbereiding.",
    },
    {
      "@type": "Offer",
      name: "Begeleid Defensie",
      url: "https://missionmovement.nl/pricing",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      price: "189",
      description:
        "Persoonlijke begeleiding richting Defensie met militaire voorbereiding.",
    },
    {
      "@type": "Offer",
      name: "Begeleid Veiligheidsdiensten",
      url: "https://missionmovement.nl/pricing",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      price: "189",
      description:
        "Persoonlijke begeleiding richting politie, brandweer, KMar of veiligheidsdiensten.",
    },
  ],
};

const Aanbod = ({ animateOnMount = true }) => {
  const { ref: enlistRef, visible: enlistVisible } = useFadeIn();

  return (
    <>
      <Helmet>
        <title>
          Aanbod & Prijzen | Mission Movement Voorbereidingsprogramma
        </title>
        <meta
          name="description"
          content="Bekijk het Mission Movement aanbod voor kandidaten die serieus richting Defensie, politie, brandweer, KMar of specialistische eenheden willen werken."
        />
        <meta
          property="og:title"
          content="Aanbod & Prijzen | Mission Movement Voorbereidingsprogramma"
        />
        <meta
          property="og:description"
          content="Bekijk het Mission Movement aanbod voor kandidaten die serieus richting Defensie, politie, brandweer, KMar of specialistische eenheden willen werken."
        />
        <meta
          property="og:image"
          content="https://missionmovement.nl/img/mmvmtlogo.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:url"
          content="https://missionmovement.nl/pricing"
        />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Aanbod & Prijzen | Mission Movement Voorbereidingsprogramma"
        />
        <meta
          name="twitter:description"
          content="Bekijk het Mission Movement aanbod voor kandidaten die serieus richting Defensie, politie, brandweer, KMar of specialistische eenheden willen werken."
        />
        <meta
          name="twitter:image"
          content="https://missionmovement.nl/img/mmvmtlogo.png"
        />
        <link
          rel="canonical"
          href="https://missionmovement.nl/pricing"
        />
        <script type="application/ld+json">
          {JSON.stringify(pricingServiceSchema)}
        </script>
      </Helmet>

      {/* Pricing hero */}
      <section className="bg-mmPage px-5 sm:px-10 lg:px-20 pt-10 pb-2 md:pt-14 md:pb-4">
        <div className="max-w-5xl mx-auto">
          <p className="font-display uppercase tracking-[0.24em] text-mmAccent text-xs md:text-sm mb-4">
            Aanbod & Prijzen
          </p>
          <h1 className="font-display uppercase tracking-[0.04em] text-white text-[36px] sm:text-[48px] md:text-[58px] leading-[0.95] max-w-[16ch]">
            Kies je route. Begin gericht.
          </h1>
          <div className="h-[3px] w-12 rounded-full bg-mmAccent mt-5 mb-5" />
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-2xl">
            Zelfstandig of begeleid. Richting Defensie of veiligheidsdiensten.
            Elk traject is 12 weken, gebouwd op echte eisen, niet op trends.
            Weet je nog niet waar je moet beginnen? Doe eerst de gratis check.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/voorbereiding-check" className="mm-btn inline-flex justify-center sm:justify-start">
              Doe eerst de gratis check
            </Link>
          </div>
          <div className="mt-10 border-t border-mmBorder" />
        </div>
      </section>

      <OfferCards variant="all" context="pricing" />

      <PerformanceBufferSection
        variant="pricing"
        compact
        showProfile={false}
        className="py-12 md:py-16"
      />

      <section className="w-full bg-mmPage px-5 sm:px-10 lg:px-20 pb-6">
        <div className="border-l-4 border-mmAccent bg-mmSurface max-w-5xl mx-auto px-5 py-5 md:px-7 md:py-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-3xl">
              <h2 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl">
                Nog niet zeker waar je moet beginnen?
              </h2>
              <p className="mt-2 text-sm md:text-base text-mmTextMuted leading-relaxed">
                Start met de gratis voorbereidings-check. Je krijgt inzicht in
                je fysieke basis, belastbaarheid, herstel en
                trainingsstructuur.
              </p>
            </div>
            <Link
              to="/voorbereiding-check"
              className="mm-btnPrimary inline-flex justify-center shrink-0"
            >
              Start de check
            </Link>
          </div>
        </div>
      </section>

      <QuoteBlock
        quote="De meeste mensen zullen nooit voelen hoe het is om een plek te verdienen binnen iets groters. Jij wel."
        author="Mission Movement"
      />

      <section className="w-full bg-mmPage py-20 px-4 flex flex-col items-center">
        <h2 className="mm-h2 text-mmText text-center text-xl md:text-3xl mb-6">
          Een glimp van de grind. Kijk goed waar je instapt.
        </h2>
        <div className="w-full flex justify-center">
          <div className="w-full max-w-3xl aspect-video rounded-2xl overflow-hidden border border-mmBorder shadow-sm bg-black">
            <VideoPreview videoId="YHffzTVE_9Q" />
          </div>
        </div>
      </section>

      <p
        ref={enlistRef}
        className={`mt-16 mb-10 text-mmAccent text-center text-base md:text-lg tracking-wide italic transition-all duration-1000 ease-out ${
          !animateOnMount || enlistVisible
            ? "opacity-100 translate-y-0 blur-0 scale-100"
            : "opacity-0 translate-y-4 blur-sm scale-95"
        }`}
      >
        “Meld je aan. De missie begint op het moment dat jij je toelegt.”
      </p>

      <section className="w-full bg-mmPage px-5 sm:px-10 lg:px-20 pb-20">
        <div className="mm-card max-w-4xl mx-auto p-6 md:p-8 text-center">
          <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm mb-3">
            Ervaringen
          </p>
          <h2 className="mm-h2 text-mmText mb-4">
            Lees ervaringen van anderen
          </h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-6">
            Bekijk hoe anderen begeleiding, structuur en voorbereiding hebben
            ervaren via Mission Movement.
          </p>
          <Link to="/ervaringen" className="mm-btn inline-flex">
            Bekijk ervaringen
          </Link>
        </div>
      </section>
    </>
  );
};

export default Aanbod;
